import { convertToLocalDateTime } from "components/helpers";
import { isArray, startCase } from "lodash";

import { Checkbox } from "@/components/ui/checkbox";
import { CASE_TIERS } from "constants/Case";
import { getCountryByCode } from "helpers/countries";
import { CasesTableActions } from "routes/case/CasesTableActions";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { TableLink } from "components/BaseTableV3/TableLink";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import TagList from "components/TagList";
import CampaignService from "services/Campaign";
import EntityService from "services/Entity";
import CaseService from "services/Case";
import ProductLineService from "services/ProductLine";
import SourceStatusService from "services/SourceStatus";
import { CASES_TABLE_CONSTANTS } from "routes/case/CasesTableV3";
import CampaignStatusModal from "routes/campaign/components/CampaignStatusModal";
import {
  NEGATABLE_OPERATORS,
  NULLABLE_OPERATORS,
} from "components/NewAdvancedSearch/filters/selectOperators";

const findCampaignCase = (original, campaignId) => {
  return original.campaign_cases.find(
    c => c.campaign_id === parseInt(campaignId)
  );
};

export const getCasesTableColumns = ({
  campaignId = null,
  invalidateData,
  deleteCase,
  enableAegisAutomationStatus = false,
}) => {
  const baseColumns = [
    {
      id: SELECT_COLUMN_ID,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "Case ID",
      id: "Case ID",
      accessorKey: "id",
      cell: ({ row }) => (
        <TableLink href={`/cases/${row.getValue("Case ID")}`}>
          {row.getValue("Case ID")}
        </TableLink>
      ),
    },
    {
      header: "External Case ID",
      id: "External Case ID",
      accessorKey: "case_id",
      size: 80,
      cell: ({ row }) => (
        <TableLink href={`/cases/${row.getValue("Case ID")}`}>
          {row.getValue("External Case ID")}
        </TableLink>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType={CASES_TABLE_CONSTANTS.className}
          modelId={row.original.id}
          tableCell
          onUpdate={invalidateData}
        />
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <TableLink href={`/cases/${row.getValue("Case ID")}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Organization",
      id: "Organization",
      accessorKey: "organization",
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity.name",
      cell: ({ row }) =>
        row.original.entity_id ? (
          <TableLink href={`/entities/${row.original.entity_id}`}>
            {row.getValue("Entity")}
          </TableLink>
        ) : null,
    },
    {
      header: "Campaigns",
      id: "Campaigns",
      accessorKey: "campaign_cases",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Campaigns").map(({ campaign_id, campaign_name }) => (
            <li key={campaign_id}>
              <TableLink href={`/campaigns/${campaign_id}`}>
                {campaign_name}
              </TableLink>
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "# Total Machines",
      id: "# Total Machines",
      accessorKey: "total_num_machines",
    },
    {
      header: "# Recent Machines",
      id: "# Recent Machines",
      accessorKey: "recent_num_machines",
    },
    {
      header: "# Approved Machines",
      id: "# Approved Machines",
      accessorKey: "approved_num_machines",
    },
    {
      header: "First Event",
      id: "First Event",
      accessorKey: "first_event",
      cell: ({ row }) => (
        <div>
          {row.getValue("First Event")
            ? convertToLocalDateTime(row.getValue("First Event")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Last Event",
      id: "Last Event",
      accessorKey: "last_event",
      cell: ({ row }) => (
        <div>
          {row.getValue("Last Event")
            ? convertToLocalDateTime(row.getValue("Last Event")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Case Tier",
      id: "Case Tier",
      accessorKey: "case_tier",
      cell: ({ row }) => {
        const value = row.getValue("Case Tier");
        return value === CASE_TIERS.UNUSABLE ? "Unusable" : value;
      },
    },
    {
      header: "Case Investigators",
      id: "Case Investigators",
      accessorKey: "case_investigators",
      cell: ({ row }) => {
        const investigators = row.original.case_investigators;
        if (!isArray(investigators)) return "N/A";
        return (
          <ScrollColumn>
            {investigators.map(ci => (
              <li key={ci.id}>{ci.name}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Investigation Status",
      id: "Investigation Status",
      accessorKey: "investigation_status.name",
    },
    {
      header: "Investigation Status Last Updated At",
      id: "Investigation Status Last Updated At",
      accessorKey: "investigation_status_last_updated_at",
      cell: ({ row }) => (
        <div>
          {row.getValue("Investigation Status Last Updated At")
            ? convertToLocalDateTime(
                row.getValue("Investigation Status Last Updated At")
              ).format("YYYY-MM-DD")
            : null}
        </div>
      ),
    },
    {
      header: "Data Score",
      id: "Data Score",
      accessorKey: "data_score",
    },
    {
      header: "Website",
      id: "Website",
      accessorKey: "website",
      cell: ({ row }) => {
        const value = row.getValue("Website");
        if (!value) return null;
        const websites = isArray(value) ? value : [value];
        return (
          <ScrollColumn>
            {websites.map((website, index) => (
              <li key={`website-${row.original.id}-${index}`}>{website}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Industry",
      id: "Industry",
      accessorKey: "industry",
    },
    {
      header: "Actionable Domain",
      id: "Actionable Domain",
      accessorKey: "actionable_domain",
    },
    {
      header: "Region",
      id: "Region",
      accessorKey: "region_name",
    },
    {
      header: "Country",
      id: "Country",
      accessorKey: "country",
      cell: ({ row }) => (
        <div>{getCountryByCode(row.getValue("Country"))?.text}</div>
      ),
    },
    {
      header: "Source Status",
      id: "Source Status",
      accessorKey: "source_status_name",
    },
    {
      header: "Product Line",
      id: "Product Line",
      accessorKey: "product_line_name",
    },
    {
      header: "Created",
      id: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => (
        <div>
          {row.getValue("Created")
            ? convertToLocalDateTime(row.getValue("Created")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Updated",
      id: "Updated",
      accessorKey: "updated_at",
      cell: ({ row }) => (
        <div>
          {row.getValue("Updated")
            ? convertToLocalDateTime(row.getValue("Updated")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Refreshed At",
      id: "refreshed_at",
      accessorKey: "refreshed_at",
      cell: ({ row }) => (
        <div>
          {row.getValue("refreshed_at")
            ? convertToLocalDateTime(row.getValue("refreshed_at")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Automation Status",
      id: "Automation Status",
      accessorKey: "automation_status",
    },
    // No cell renderer: Aegis writes this column through the API and the
    // table shows it read-only.
    ...(enableAegisAutomationStatus
      ? [
          {
            header: "Aegis Automation Status",
            id: "Aegis Automation Status",
            accessorKey: "aegis_automation_status.name",
          },
        ]
      : []),
  ];

  // Add campaign-specific columns if campaignId is provided
  const campaignColumns = campaignId
    ? [
        {
          header: "Campaign Status",
          id: "Campaign Status",
          accessorKey: "campaign_status",
          enableSorting: false,
          cell: ({ row }) => {
            const campaignCase = findCampaignCase(row.original, campaignId);
            return campaignCase ? (
              <CampaignStatusModal
                campaignStatus={
                  campaignCase?.campaign_status || { id: 0, name: "" }
                }
                campaignId={parseInt(campaignId)}
                caseId={row.original.id}
                updateData={invalidateData}
                effectiveDate={campaignCase?.effective_date}
              />
            ) : (
              "N/A"
            );
          },
        },
        {
          header: "Effective Date",
          id: "Effective Date",
          accessorKey: "campaign_status_effective_date",
          enableSorting: false,
          cell: ({ row }) => {
            const campaignCase = findCampaignCase(row.original, campaignId);
            return campaignCase?.effective_date || "N/A";
          },
        },
        {
          header: "Campaign Assignee",
          id: "Campaign Assignee",
          accessorKey: "campaign_assignee",
          enableSorting: false,
          cell: ({ row }) => {
            const campaignCase = findCampaignCase(row.original, campaignId);
            return campaignCase ? campaignCase.assigned_user_name : "N/A";
          },
        },
      ]
    : [];

  // Add actions column
  const actionsColumn = {
    header: <div></div>,
    id: "Actions",
    accessorKey: "actions",
    size: 60,
    enableSorting: false,
    enableResizing: false,
    meta: {
      isDisplay: true,
    },
    cell: ({ row }) => (
      <CasesTableActions
        caseId={row.getValue("Case ID")}
        caseData={row.original}
        fetchCases={invalidateData}
        onConfirmDelete={deleteCase}
        campaignId={campaignId}
      />
    ),
  };

  return [...baseColumns, ...campaignColumns, actionsColumn];
};

export const getCasesTableFilters = ({
  regions,
  countries,
  users,
  investigationStatuses,
  tags,
  campaignId,
  entityId,
  enableAegisAutomationStatus = false,
}) => {
  return [
    {
      key: "first_event",
      title: "First Event Date",
      type: "dateRange",
    },
    {
      key: "last_event",
      title: "Last Event Date",
      type: "dateRange",
    },
    {
      key: "refreshed_at",
      title: "Refreshed At",
      type: "dateRange",
    },
    ...(campaignId
      ? [
          {
            key: "effective_date",
            title: "Effective Date",
            type: "dateRange",
          },
        ]
      : []),
    {
      key: "case_tier",
      title: "Case Tier",
      type: "select",
      operators: NEGATABLE_OPERATORS,
      data: Object.keys(CASE_TIERS).map(key => {
        return {
          id: CASE_TIERS[key],
          name: startCase(key.toLowerCase()),
        };
      }),
    },
    ...(campaignId
      ? []
      : [
          {
            key: "campaign_id",
            title: "Campaign",
            type: "ruvixxSelect",
            operators: NULLABLE_OPERATORS,
            queryFn: filters => {
              return CampaignService.getCampaignsForFilters({
                ...filters,
                has_case: true,
              });
            },
            props: {
              includeNoneOption: true,
            },
          },
        ]),
    ...(entityId
      ? []
      : [
          {
            key: "entity_id",
            title: "Entity",
            type: "ruvixxSelect",
            operators: NULLABLE_OPERATORS,
            queryFn: filters => {
              return EntityService.getEntitiesForFilters({
                ...filters,
                has_case: true,
              });
            },
          },
        ]),
    {
      key: "case_id",
      title: "Case",
      type: "ruvixxSelect",
      queryFn: filters => {
        return CaseService.getForFilters(
          entityId
            ? {
                ...filters,
                entity_id: entityId,
              }
            : filters
        );
      },
    },
    {
      key: "investigation_status_id",
      title: "Investigation Status",
      type: "select",
      data: investigationStatuses,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "case_investigators",
      title: "Case Investigators",
      type: "select",
      data: users,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "total_num_machines",
      title: "# Total Machines",
      type: "range",
    },
    {
      key: "recent_num_machines",
      title: "# Recent Machines",
      type: "range",
    },
    {
      key: "approved_num_machines",
      title: "# Approved Machines",
      type: "range",
    },
    {
      key: "data_score",
      title: "Data Score",
      type: "range",
    },
    {
      key: "search:actionable_domain",
      title: "Actionable Domain",
      type: "input",
    },
    {
      key: "website",
      title: "Website",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: filters => {
        return CaseService.getWebsitesForFilters(filters);
      },
    },
    {
      key: "regions",
      title: "Region",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: regions,
    },
    {
      key: "countries",
      title: "Country",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: countries,
    },
    {
      key: "source_statuses",
      title: "Source Status",
      type: "ruvixxSelect",
      queryFn: SourceStatusService.getForFilters,
    },
    {
      key: "product_lines",
      title: "Product Line",
      type: "ruvixxSelect",
      operators: NEGATABLE_OPERATORS,
      queryFn: ProductLineService.getForFilters,
    },
    {
      key: "automation_status",
      title: "Automation Status",
      type: "select",
      operators: NEGATABLE_OPERATORS,
      data: [
        { id: "approvable", name: "Approvable" },
        { id: "rejected", name: "Rejected" },
        { id: "unset", name: "Unset" },
      ],
    },
    ...(enableAegisAutomationStatus
      ? [
          {
            key: "aegis_automation_status_id",
            title: "Aegis Automation Status",
            type: "select",
            data: investigationStatuses,
            props: {
              includeNoneOption: true,
            },
          },
        ]
      : []),
  ];
};
